import { useEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, ChevronLeft, ChevronRight, Mail, Search, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { formatDateTime } from '@/lib/dates'
import { formatTicketRef } from '@/lib/ticketRef'
import { useEmailLog } from '@/queries/emailLog'
import type { EmailLogEntry } from '@/types/emailLog'
import {
  INBOUND_STATUSES,
  OUTBOUND_STATUSES,
  counterparty,
  emailStatusLabels,
  emailStatusVariants,
  reasonLabel,
  openedByLabel,
  readersTitle,
  statusBreakdown,
} from './emailStatus'
import { EmailDetailDialog } from './EmailDetailDialog'

export const EMAIL_LOG_PAGE_SIZE = 50

// The API bounds the log to the last 90 days unless told otherwise; this is
// the small range control that widens or narrows it.
export const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
] as const
const DEFAULT_RANGE = '90'

// Start of the day, so the value (and the query key) is stable all day.
function rangeFrom(days: string): string {
  return dayjs().subtract(Number(days), 'day').startOf('day').toISOString()
}

/** Email log filters, mirrored into the /admin/email-log URL by the route. */
export interface EmailLogFilters {
  /** Days back, from RANGE_OPTIONS. Absent = the API's default of 90. */
  range?: string
  direction?: string
  status?: string
  q?: string
  ticketId?: string
  page?: number
}

// Same V2 filterbar/table classes as the admin queue (AdminQueueView).
const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'
const thClass =
  'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-4 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'
const tableCardClass =
  'overflow-x-auto rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper'
const tdClass = 'h-11 px-4 align-middle'

function DirectionCell({ entry }: { entry: EmailLogEntry }) {
  const outbound = entry.direction === 'outbound'
  const Icon = outbound ? ArrowUpRight : ArrowDownLeft
  return (
    <span className="inline-flex items-center gap-1.5 text-etn-text-soft">
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {outbound ? 'Outbound' : 'Inbound'}
    </span>
  )
}

function EmailRow({ entry, onOpen }: { entry: EmailLogEntry; onOpen: (id: string) => void }) {
  const reason = entry.status === 'unmatched' || entry.status === 'dropped' ? reasonLabel(entry.reason) : null
  const breakdown = statusBreakdown(entry)
  const openedBy = openedByLabel(entry)
  const open = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.target instanceof Element && e.target.closest('a, button')) return
    onOpen(entry._id)
  }
  return (
    <tr onClick={open} className="cursor-pointer text-etn-cell transition-colors hover:bg-etn-row-hover">
      <td className={`${tdClass} whitespace-nowrap font-normal text-etn-text-soft`}>{formatDateTime(entry.createdAt)}</td>
      <td className={`${tdClass} whitespace-nowrap`}><DirectionCell entry={entry} /></td>
      <td className={`${tdClass} max-w-[220px] truncate font-medium text-etn-text-cell`} title={entry.direction === 'outbound' ? entry.recipients.map(r => r.address).join(', ') : counterparty(entry)}>
        {counterparty(entry)}
      </td>
      <td className={`${tdClass} max-w-[340px] truncate text-etn-text`}>
        <button type="button" onClick={() => onOpen(entry._id)} className="max-w-full truncate text-left hover:underline">
          {entry.subject || '(no subject)'}
        </button>
        {reason && <small className="ml-2 font-normal text-etn-micro text-etn-warning-text">{reason}</small>}
      </td>
      <td className={`${tdClass} whitespace-nowrap`}>
        {entry.ticket ? (
          <Link to={`/admin/tickets/${entry.ticket._id}` as never} className="text-etn-micro font-semibold text-etn-accent-text-active hover:underline">
            {formatTicketRef(entry.ticket)}
          </Link>
        ) : (
          <span className="text-etn-text-soft">—</span>
        )}
      </td>
      <td className={tdClass}>
        <Badge variant={emailStatusVariants[entry.status]} title={breakdown ?? undefined}>
          {emailStatusLabels[entry.status]}
        </Badge>
      </td>
      <td className={`${tdClass} text-right tabular-nums text-etn-text-soft`}>
        {entry.direction === 'outbound' ? (
          <span className="inline-flex items-center justify-end gap-2" title={readersTitle(entry)}>
            {openedBy && <span className="text-etn-micro text-etn-success-text">{openedBy}</span>}
            <span>{entry.openCount}</span>
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  )
}

export function EmailLogView({
  filters,
  onChange,
}: {
  filters: EmailLogFilters
  onChange: (next: EmailLogFilters) => void
}) {
  // Any filter change goes back to the first page; only the pager sets `page`.
  const set = (patch: Partial<EmailLogFilters>) => onChange({ ...filters, page: undefined, ...patch })
  const [openId, setOpenId] = useState<string | undefined>()

  const [searchText, setSearchText] = useState(filters.q ?? '')
  useEffect(() => { setSearchText(filters.q ?? '') }, [filters.q])
  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchText.trim()
      if ((filters.q ?? '') !== trimmed) set({ q: trimmed || undefined })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const page = Math.max(1, filters.page ?? 1)
  const params: Record<string, unknown> = { limit: EMAIL_LOG_PAGE_SIZE, skip: (page - 1) * EMAIL_LOG_PAGE_SIZE }
  if (filters.direction) params.direction = filters.direction
  if (filters.status) params.status = filters.status
  if (filters.q) params.q = filters.q
  if (filters.ticketId) params.ticketId = filters.ticketId
  if (filters.range && filters.range !== DEFAULT_RANGE) params.from = rangeFrom(filters.range)

  const { data, isLoading, error, refetch } = useEmailLog(params)
  const rows = data?.data ?? []
  const total = data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / EMAIL_LOG_PAGE_SIZE))
  const ticketFilterLabel = filters.ticketId
    ? rows.find(r => r.ticket?._id === filters.ticketId)?.ticket ?? { _id: filters.ticketId }
    : null

  const statusOptions =
    filters.direction === 'outbound' ? OUTBOUND_STATUSES : filters.direction === 'inbound' ? INBOUND_STATUSES : [...OUTBOUND_STATUSES, ...INBOUND_STATUSES]
  // A range other than the 90-day default narrows or widens the list like any filter.
  const hasFilters = Boolean(filters.direction || filters.status || filters.q || filters.ticketId || (filters.range && filters.range !== DEFAULT_RANGE))

  return (
    <div className="flex-1 min-w-0">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-lg font-semibold text-etn-text">Email log</h1>
        <span className="text-etn-micro text-etn-text-soft tabular-nums">
          {data ? `${total} email${total === 1 ? '' : 's'}` : ''}
        </span>
        {hasFilters && (
          <button
            type="button"
            onClick={() => onChange({})}
            className="inline-flex items-center gap-1 text-etn-micro text-etn-text-soft hover:text-etn-text"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-etn-text-soft" />
          <Input
            pill
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search address or subject…"
            aria-label="Search email log"
            className="pl-9"
          />
        </div>
        <select
          aria-label="Direction"
          className={filterSelectClass}
          value={filters.direction ?? ''}
          onChange={e => {
            const direction = e.target.value || undefined
            const keepStatus = !direction || (direction === 'outbound' ? OUTBOUND_STATUSES : INBOUND_STATUSES).includes(filters.status as never)
            set({ direction, status: keepStatus ? filters.status : undefined })
          }}
        >
          <option value="">Both directions</option>
          <option value="outbound">Outbound</option>
          <option value="inbound">Inbound</option>
        </select>
        <select
          aria-label="Date range"
          className={filterSelectClass}
          value={filters.range ?? DEFAULT_RANGE}
          onChange={e => set({ range: e.target.value === DEFAULT_RANGE ? undefined : e.target.value })}
        >
          {RANGE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          aria-label="Status"
          className={filterSelectClass}
          value={filters.status ?? ''}
          onChange={e => set({ status: e.target.value || undefined })}
        >
          <option value="">All statuses</option>
          {statusOptions.map(s => (
            <option key={s} value={s}>{emailStatusLabels[s]}</option>
          ))}
        </select>
        {ticketFilterLabel && (
          <span className="inline-flex h-[var(--etn-control-height-input)] items-center gap-1.5 rounded-etn-pill bg-etn-accent-tint pl-4 pr-2 text-etn-control font-semibold text-etn-accent-text-active">
            Ticket {formatTicketRef(ticketFilterLabel)}
            <button
              type="button"
              aria-label="Remove ticket filter"
              onClick={() => set({ ticketId: undefined })}
              className="rounded-etn-pill p-1 hover:bg-etn-accent-tint-hover"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
      </div>

      {isLoading && <ListSkeleton rows={6} />}

      {error && (
        <div className="flex flex-col items-center gap-3 py-16">
          <EmptyState icon={<AlertTriangle />} title="Couldn't load the email log." body="Something went wrong. Please try again." />
          <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        hasFilters
          ? <EmptyState icon={<Mail />} title="No emails match." body="Try widening the filters." />
          : <EmptyState icon={<Mail />} title="No email yet." body="Notifications we send and email we receive will show up here." />
      )}

      {!isLoading && !error && rows.length > 0 && (
        <>
          <div className={tableCardClass}>
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th scope="col" className={thClass}>Time</th>
                  <th scope="col" className={thClass}>Direction</th>
                  <th scope="col" className={thClass}>From / To</th>
                  <th scope="col" className={thClass}>Subject</th>
                  <th scope="col" className={thClass}>Ticket</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={`${thClass} text-right`}>Opens</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-etn-border-row">
                {rows.map(entry => <EmailRow key={entry._id} entry={entry} onOpen={setOpenId} />)}
              </tbody>
            </table>
          </div>
          {lastPage > 1 && (
            <nav aria-label="Email log pages" className="mt-4 flex items-center justify-end gap-2 text-etn-micro text-etn-text-soft">
              <span className="tabular-nums">Page {page} of {lastPage}</span>
              <Button size="sm" variant="outline" aria-label="Previous page" disabled={page <= 1} onClick={() => onChange({ ...filters, page: page - 1 === 1 ? undefined : page - 1 })}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" aria-label="Next page" disabled={page >= lastPage} onClick={() => onChange({ ...filters, page: page + 1 })}>
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </nav>
          )}
        </>
      )}

      <EmailDetailDialog id={openId} onClose={() => setOpenId(undefined)} />
    </div>
  )
}
